const defaultPublicDomainSuffix = "pitaliy.com";

const PUBLIC_DOMAIN_SUFFIX =
  import.meta.env.VITE_PARENT_SITE_PUBLIC_DOMAIN_SUFFIX ||
  defaultPublicDomainSuffix;

/**
 * Returns true when hostname is a public subdomain of the given suffix (e.g. aivy.pitaliy.com).
 * Returns false for bare domain (pitaliy.com) or non-matching host (localhost, other domains).
 *
 * @param hostname - Hostname to check (e.g. from URL)
 * @returns Whether the hostname is a non-empty subdomain of PUBLIC_DOMAIN_SUFFIX
 */
export function isFromPublicSubdomain(hostname: string): boolean {
  if (!hostname) return false;
  if (hostname === PUBLIC_DOMAIN_SUFFIX) return false;
  if (!hostname.endsWith(`.${PUBLIC_DOMAIN_SUFFIX}`)) return false;
  const subdomainPart = hostname.slice(0, -(PUBLIC_DOMAIN_SUFFIX.length + 1));
  return subdomainPart.length > 0;
}

/**
 * Returns the public subdomain part (e.g. "aivy" from "aivy.pitaliy.com"), or null if not a public subdomain.
 * @param hostname - Hostname to parse
 * @returns Subdomain string or null
 */
export function getPublicSubdomainPart(hostname: string): string | null {
  if (!hostname || hostname === PUBLIC_DOMAIN_SUFFIX) return null;
  if (!hostname.endsWith(`.${PUBLIC_DOMAIN_SUFFIX}`)) return null;
  const subdomainPart = hostname.slice(0, -(PUBLIC_DOMAIN_SUFFIX.length + 1));
  return subdomainPart.length > 0 ? subdomainPart : null;
}

/**
 * Safely parses URL and returns hostname, or null if invalid.
 * @param urlString - URL string from parent (e.g. receivedUrl)
 * @returns Hostname or null when URL is invalid
 */
export function getHostnameFromUrl(urlString: string): string | null {
  try {
    return new URL(urlString).hostname;
  } catch {
    return null;
  }
}
